/**
 * UsernamePrompt — Full-screen overlay prompting the player for a callsign
 * AND a game mode (Free-for-all vs Battle Royale). The FIRST thing players
 * see when the game loads.
 */
import { GameMode } from '../../shared/types.js';
export { GameMode };

interface ModeOption {
  mode: GameMode;
  title: string;
  subtitle: string;
}

const MODE_OPTIONS: ModeOption[] = [
  { mode: GameMode.Free, title: 'FREE FOR ALL', subtitle: 'Respawn. Leaderboard. Ongoing.' },
  { mode: GameMode.Br, title: 'BATTLE ROYALE', subtitle: 'Scatter. Shrink. Last drone standing.' },
];

export class UsernamePrompt {
  private overlay: HTMLElement;
  private input: HTMLInputElement;
  private submitBtn: HTMLButtonElement;
  private onSubmitCb: ((username: string, mode: GameMode) => void) | null = null;
  private selectedMode: GameMode = GameMode.Free;
  private modeElements: Map<GameMode, HTMLElement> = new Map();

  constructor(private parentElement: HTMLElement) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'username-overlay hidden';

    const card = document.createElement('div');
    card.className = 'username-card';

    const title = document.createElement('div');
    title.className = 'username-title';
    title.textContent = 'DRONE SWARM';
    card.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'username-subtitle';
    subtitle.textContent = 'Enter the arena';
    card.appendChild(subtitle);

    const modePicker = document.createElement('div');
    modePicker.className = 'mode-picker';
    for (const opt of MODE_OPTIONS) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'mode-option';
      el.dataset.mode = opt.mode;

      const t = document.createElement('div');
      t.className = 'mode-option-title';
      t.textContent = opt.title;
      el.appendChild(t);

      const s = document.createElement('div');
      s.className = 'mode-option-subtitle';
      s.textContent = opt.subtitle;
      el.appendChild(s);

      el.addEventListener('click', () => this.setMode(opt.mode));

      this.modeElements.set(opt.mode, el);
      modePicker.appendChild(el);
    }
    card.appendChild(modePicker);

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.className = 'username-input';
    this.input.placeholder = 'Choose your callsign...';
    this.input.maxLength = 16;
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    card.appendChild(this.input);

    this.submitBtn = document.createElement('button');
    this.submitBtn.className = 'username-submit';
    this.submitBtn.textContent = 'DEPLOY';
    card.appendChild(this.submitBtn);

    const controls = document.createElement('div');
    controls.className = 'username-controls';
    controls.innerHTML =
      '<span>WASD</span> move &bull; <span>Space/Shift</span> altitude &bull; ' +
      '<span>1-6</span> cards &bull; <span>Click</span> to lock cursor &bull; ' +
      '<span>ESC</span> unlock &bull; <span>Right-drag</span> or two-finger swipe to rotate';
    card.appendChild(controls);

    this.overlay.appendChild(card);
    this.parentElement.appendChild(this.overlay);

    this.setMode(this.selectedMode);

    this.submitBtn.addEventListener('click', () => this.handleSubmit());
    this.input.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') this.handleSubmit();
    });
  }

  /** Display the prompt and invoke callback when player submits a valid name. */
  show(onSubmit: (username: string, mode: GameMode) => void, presetMode?: GameMode): void {
    this.onSubmitCb = onSubmit;
    if (presetMode) this.setMode(presetMode);
    this.overlay.classList.remove('hidden', 'hiding');
    this.input.value = '';
    // Delay focus so the animation plays first
    requestAnimationFrame(() => this.input.focus());
  }

  /** Fade out and hide. */
  hide(): void {
    this.overlay.classList.add('hiding');
    const onEnd = () => {
      this.overlay.classList.add('hidden');
      this.overlay.classList.remove('hiding');
      this.overlay.removeEventListener('animationend', onEnd);
    };
    this.overlay.addEventListener('animationend', onEnd);
  }

  private setMode(mode: GameMode): void {
    this.selectedMode = mode;
    for (const [m, el] of this.modeElements) {
      el.classList.toggle('selected', m === mode);
    }
  }

  private handleSubmit(): void {
    const value = this.input.value.trim();
    if (value.length === 0 || value.length > 16) {
      this.input.style.borderColor = 'var(--damage)';
      setTimeout(() => {
        this.input.style.borderColor = '';
      }, 600);
      return;
    }
    this.onSubmitCb?.(value, this.selectedMode);
  }
}
